import React, { ComponentPropsWithoutRef, FC, useEffect, useRef } from 'react';
import HC, { Chart, ChartCallbackFunction, Options } from 'highcharts';
import merge from 'lodash/merge';
import cx from 'classnames';

import { Override } from 'types/common';

import 'highcharts/css/highcharts.css';

import styles from './styles.module.css';

const defaults: Options = {
  chart: {
    styledMode: true,
    spacing: [10, 10, 10, 10],
    zooming: {
      mouseWheel: false,
    },
  },
  accessibility: {
    enabled: false,
  },
  credits: {
    enabled: false,
  },
  legend: {
    enabled: false,
  },
  plotOptions: {
    series: {
      marker: {
        radius: 2,
      },
    },
  },
  title: {
    text: undefined,
  },
  tooltip: {
    enabled: false,
  },
  xAxis: {
    title: undefined,
    lineWidth: 0,
    labels: {
      distance: 8,
    },
  },
  yAxis: {
    title: undefined,
    labels: {
      distance: 8,
      reserveSpace: true,
    },
  },
};

type HighchartsProps = Override<ComponentPropsWithoutRef<'div'>, {
  type?: 'chart' | 'stockChart';
  options: Options;
  modules?: Array<(highcharts: typeof HC) => void>;
  onChart?: ChartCallbackFunction;
}>;

const Highcharts: FC<HighchartsProps> = ({
  className,
  type = 'chart',
  options,
  modules,
  onChart,
  ...props
}) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<Chart | null>(null);

  useEffect(() => {
    if (!rootRef.current) {
      return;
    }

    const settings = merge({}, defaults, options);

    if (!chartRef.current) {
      modules?.forEach((module) => module(HC));

      if (typeof HC[type] !== 'function') {
        throw new Error(`Required module for the type "${type}" is not imported.`);
      }

      chartRef.current = HC[type](rootRef.current, settings, onChart);
    } else {
      chartRef.current.update(settings, true, true);
    }
  }, [type, options, modules, onChart]);

  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, []);

  return (
    <div
      className={cx('highcharts-light', styles.root, className)}
      ref={rootRef}
      {...props}
    />
  );
};

export default Highcharts;
